import { ADMIN_MANAGE_COLLECTION_PATH } from 'routes/paths'
import {
  dropdownItemsAssessments,
  dropdownItemsManage,
  dropdownItemsPlanning,
  dropdownItemsReportsDoActivityV2,
  dropdownItemsReportsParanaNotice,
  dropdownItemsReportsV2
} from './dropdownItems'
import type { DropdownMenuItemsProps, SideBarV2Props } from './types'
import type { UserState } from 'utils/hooks/use-userInfo'

const HYBRID_OFFER_PATH = '/app-v2/admin/hybrid-offer'
const FAMILIES_ACCESS_PATH = '/app-v2/admin/familias'
const FAMILIES_REPORTED_BOOKS_PATH = '/app-v2/admin/familias/livros-reportados'
const FAMILY_SCHOOL_PANEL_FLAG = 'familyschoolpanel'

const normalizeFlagKey = (key: string) => key.replace(/[_-]/g, '').toLowerCase()

export const isFamilySchoolPanelEnabled = (
  featureToggles: SideBarV2Props['featureToggles']
): boolean =>
  Object.entries(
    (featureToggles ?? {}) as Record<string, boolean | undefined>
  ).some(
    ([key, value]) =>
      value === true && normalizeFlagKey(key) === FAMILY_SCHOOL_PANEL_FLAG
  )

const familiesSection = (t: SideBarV2Props['t']): DropdownMenuItemsProps => ({
  id: 5,
  title: t('Famílias'),
  iconName: 'user-heart',
  items: [
    { title: t('Acessos das famílias'), path: FAMILIES_ACCESS_PATH },
    { title: t('Livros reportados'), path: FAMILIES_REPORTED_BOOKS_PATH }
  ]
})

const acervoSection = (
  user: UserState,
  canManageCollections: boolean,
  showPhysicalBooks: boolean,
  t: SideBarV2Props['t']
): DropdownMenuItemsProps => {
  const items = [
    user.canAccessCustomCollections &&
      canManageCollections && {
        title: t('Livros digitais'),
        path: ADMIN_MANAGE_COLLECTION_PATH
      },
    showPhysicalBooks && {
      title: t('Livros físicos'),
      path: HYBRID_OFFER_PATH
    }
  ]

  return {
    id: 4,
    title: t('Acervo'),
    iconName: 'book-open',
    items: items.filter(Boolean)
  }
}

export const sideBarItems = ({
  featureToggles,
  user,
  canManageCollections,
  canManagePermissions,
  hasTeacherId,
  isSuperAutorEligible,
  isHybridOfferEnabled,
  isMobile,
  t
}: SideBarV2Props): DropdownMenuItemsProps[] => {
  const acervo = acervoSection(
    user,
    canManageCollections,
    Boolean(isHybridOfferEnabled) && !isMobile,
    t
  )
  const showAcervo = (acervo.items?.length ?? 0) > 0
  const families = isFamilySchoolPanelEnabled(featureToggles)
    ? [familiesSection(t)]
    : []

  if (featureToggles.doActivityV2) {
    return [
      {
        id: 0,
        title: t('Relatórios'),
        iconName: 'chart-bar',
        items: dropdownItemsReportsDoActivityV2(
          user,
          featureToggles,
          t,
          hasTeacherId
        )
      },
      {
        id: 1,
        title: t('Engajamento'),
        iconName: 'chalkboard',
        items: dropdownItemsPlanning(user, t)
      },
      {
        id: 2,
        title: t('Avaliações'),
        iconName: 'clipboard-pencil',
        items: dropdownItemsAssessments(user, featureToggles, t)
      },
      ...(showAcervo ? [acervo] : []),
      ...families,
      {
        id: 3,
        title: t('Gerenciamento'),
        iconName: 'hierarchy',
        items: dropdownItemsManage(
          user,
          canManagePermissions,
          t,
          isSuperAutorEligible,
          featureToggles.unifyRolesAndPermissions
        )
      }
    ]
  }

  if (featureToggles.reportsV2) {
    return [
      {
        id: 0,
        title: t('Relatórios'),
        iconName: 'chart-bar',
        items: dropdownItemsReportsParanaNotice(t, user)
      },
      {
        id: 2,
        title: t('Avaliações'),
        iconName: 'clipboard-pencil',
        items: dropdownItemsAssessments(user, featureToggles, t)
      },
      ...(showAcervo ? [acervo] : []),
      ...families
    ]
  }

  return [
    {
      id: 0,
      title: t('Relatórios'),
      iconName: 'chart-bar',
      items: dropdownItemsReportsV2(user, featureToggles, t, hasTeacherId)
    },
    {
      id: 1,
      title: t('Engajamento'),
      iconName: 'chalkboard',
      items: dropdownItemsPlanning(user, t)
    },
    {
      id: 2,
      title: t('Avaliações'),
      iconName: 'clipboard-pencil',
      items: dropdownItemsAssessments(user, featureToggles, t)
    },
    ...(showAcervo ? [acervo] : []),
    ...families,
    {
      id: 3,
      title: t('Gerenciamento'),
      iconName: 'hierarchy',
      items: dropdownItemsManage(
        user,
        canManagePermissions,
        t,
        isSuperAutorEligible,
        featureToggles.unifyRolesAndPermissions
      )
    }
  ]
}
